import PropTypes from "prop-types";
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";

import { Button } from "@/components/ui/button";

import { Input } from "@/components/ui/input";

import { Label } from "@/components/ui/label";

import {

  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,

} from "@/components/ui/dialog";

import { SourceStatusBadge } from "@/components/brain/SourceStatusBadge";

import { KnowledgeSourceSetupWizard } from "@/components/brain/KnowledgeSourceSetupWizard";
import { CloudImportWizard } from "@/components/brain/CloudImportWizard";

import { isSetupComplete, clearProgress, needsSetupContinuation } from "@/components/brain/wizardState";

import { brainApi, downloadBrainDocument } from "@/lib/api";

import {
  FILE_UPLOAD_ACCEPT,
  FILE_UPLOAD_FORMATS_LABEL,
  knowledgeUploadRejectToast,
  partitionKnowledgeUploadFiles,
} from "@/lib/knowledgeFileTypes";

import { Upload, Trash2, RefreshCw, Download, Pencil, Search, Users, FileText } from 'lucide-react';

import { toast } from "sonner";



function formatBytes(n) {

  if (!n) return "—";

  if (n < 1024) return `${n} B`;

  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;

  return `${(n / (1024 * 1024)).toFixed(1)} MB`;

}

export function formatKbSearchResultContent(text) {
  if (!text) return "";
  let s = String(text);

  // Remove Markdown headers (e.g. ### FAQ-169: -> FAQ-169:)
  s = s.replace(/^#{1,6}\s+/gm, "");
  s = s.replace(/#{1,6}\s+/g, "");

  // Remove Markdown bold / italic / inline code markers (**bold**, *italic*, `code`)
  s = s.replace(/[*_]{1,3}([^*_]+)[*_]{1,3}/g, "$1");
  s = s.replace(/`([^`]+)`/g, "$1");

  // Remove Markdown links [text](url) -> text, and images ![alt](url) -> alt
  s = s.replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1");
  s = s.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");

  // Format Markdown tables: replace pipe dividers with middle dot separator
  s = s.replace(/[\s|\\-]*\|[\s|\\-]*/g, " · ");

  // Clean up leading list symbols (e.g., "- 11-06" or "* item")
  s = s.replace(/^[\s\-*+]+/gm, "");

  // Normalize multiple dots / spaces into clean readable text
  s = s.replace(/(?:\s*·\s*){2,}/g, " · ");
  s = s.replace(/^[\s·-]+|[\s·-]+$/g, "");
  s = s.replace(/\s{2,}/g, " ");

  return s.trim();
}



function statusLabel(status) {

  const map = {

    uploading: "Uploading",

    processing: "Processing",

    indexing: "Indexing",

    ready: "Ready",

    failed: "Failed",

  };

  return map[status] || status;

}



function syncLabel(status) {
  const map = {
    pending: "Pending sync",
    running: "Syncing…",
    completed: "Ready",
    failed: "Sync failed",
    skipped: "Indexed — gateway offline",
  };
  return map[status] || status;
}

function resolveBadgeStatus(s) {
  if (s.openclaw_sync_status === "completed" || s.openclaw_sync_status === "skipped") {
    return "connected";
  }
  if (s.openclaw_sync_status === "failed" || s.status === "failed") return "not_connected";
  return "not_configured";
}

export const CustomSourceManager = forwardRef(function CustomSourceManager({ sources = [], onChanged, headless = false }, ref) {

  const [wizardOpen, setWizardOpen] = useState(false);

  const [wizardSourceId, setWizardSourceId] = useState(null);

  const [cloudWizardOpen, setCloudWizardOpen] = useState(false);

  const [cloudProvider, setCloudProvider] = useState(null);

  const [cloudInitialConnected, setCloudInitialConnected] = useState(false);

  const [detailId, setDetailId] = useState(null);

  const [detail, setDetail] = useState(null);

  const [rename, setRename] = useState("");

  const [busy, setBusy] = useState(false);

  const [searchQ, setSearchQ] = useState("");

  const [searchHits, setSearchHits] = useState([]);

  const uploadMoreRef = useRef(null);



  const silentRefresh = useCallback(async () => {

    if (onChanged) await onChanged({ silent: true });

  }, [onChanged]);



  const showDetail = async (id) => {

    setDetailId(id);

    setSearchHits([]);

    setSearchQ("");

    const d = await brainApi.getCustomSource(id);

    setDetail(d);

    setRename(d.name);

  };

  const runShowDetail = (id) => {
    showDetail(id).catch((e) => {
      toast.error(e?.message || "Couldn't load knowledge source");
    });
  };



  const openWizard = (sourceId = null) => {

    setWizardSourceId(sourceId);

    setWizardOpen(true);

  };



  useImperativeHandle(ref, () => ({

    openSetup(sourceId) {

      if (!sourceId) {
        const savedDrafts = sources.filter(
          (s) => s.wizard_session?.saved && !s.wizard_session?.completed,
        );
        if (savedDrafts.length === 1) {
          openWizard(savedDrafts[0].id);
          return;
        }
        openWizard(null);
        return;
      }

      brainApi.getCustomSource(sourceId).then((detail) => {

        if (isSetupComplete(detail)) {

          runShowDetail(detail.id);

        } else {

          openWizard(detail.id);

        }

      }).catch(() => openWizard(sourceId));

    },

    openCreate() {

      openWizard(null);

    },

    openUpload(sourceId) {

      if (sourceId) {
        brainApi.saveWizardSession(sourceId, { step: 2, force_upload: true, saved: true }).catch(() => {});
      }

      openWizard(sourceId);

    },

    openDetail: (sourceId) => {

      runShowDetail(sourceId);

    },

    openManage: (sourceId) => {

      if (sourceId) {
        runShowDetail(sourceId);
        return;
      }

      if (sources.length === 1) {
        runShowDetail(sources[0].id);
        return;
      }

      const withDocs = sources.find((s) => (s.document_count ?? 0) > 0);
      if (withDocs) {
        runShowDetail(withDocs.id);
        return;
      }

      openWizard(null);

    },

    openCloudImport(provider, { connected = false } = {}) {

      setCloudProvider(provider);

      setCloudInitialConnected(connected);

      setCloudWizardOpen(true);

    },

  }), [sources, runShowDetail]);



  const refreshDetail = useCallback(async () => {

    await silentRefresh();

    if (detailId) {

      const d = await brainApi.getCustomSource(detailId);

      setDetail(d);

      setRename(d.name);

    }

  }, [detailId, silentRefresh]);



  const handleSync = async (id) => {

    setBusy(true);

    try {

      const res = await brainApi.syncCustomSource(id);

      const st = res.openclaw_sync?.status;

      if (st === "completed") {

        const mode = res.openclaw_sync?.sync_mode;

        if (mode === "sellix_rag" || res.openclaw_sync?.workspace_sync === "failed") {

          toast.success("Knowledge ready for agents", {

            description: res.openclaw_sync?.reason || "Indexed in Sellyx Brain and available via search.",

          });

        } else {

          toast.success("Synced to OpenClaw agent");

        }

      } else if (st === "skipped") {

        toast.message("Indexed — gateway offline", { description: res.openclaw_sync?.reason });

      } else if (st === "failed") {

        toast.error("OpenClaw sync failed", { description: res.openclaw_sync?.reason });

      }

      await refreshDetail();

    } catch (e) {

      toast.error(e.message || "Sync failed");

    } finally {

      setBusy(false);

    }

  };



  const handleDeleteSource = async (id) => {

    if (!window.confirm("Delete this knowledge source and all indexed content?")) return;

    setBusy(true);

    try {

      await brainApi.deleteCustomSource(id);

      toast.success("Knowledge source deleted");

      clearProgress(id);

      if (detailId === id) {

        setDetailId(null);

        setDetail(null);

      }

      await silentRefresh();

    } catch (e) {

      toast.error(e.message || "Delete failed");

    } finally {

      setBusy(false);

    }

  };



  const handleRename = async () => {

    if (!detailId || !rename.trim()) return;

    setBusy(true);

    try {

      await brainApi.renameCustomSource(detailId, { name: rename.trim() });

      toast.success("Renamed");

      await refreshDetail();

    } catch (e) {

      toast.error(e.message || "Rename failed");

    } finally {

      setBusy(false);

    }

  };



  const handleSearch = async () => {

    if (!searchQ.trim()) {
      setSearchHits([]);
      return;
    }

    setBusy(true);

    try {

      const res = await brainApi.search({

        query: searchQ.trim(),

        source_id: detailId || undefined,

        limit: 10,

      });

      setSearchHits(res.results || []);

    } catch (e) {

      toast.error(e.message || "Search failed");

    } finally {

      setBusy(false);

    }

  };



  const handleWizardComplete = async () => {

    await silentRefresh();

  };



  return (

    <>

      {!headless && (

        <div className="space-y-4" data-testid="custom-source-manager">

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">

            <div className="min-w-0">

              <div className="text-[13px] font-semibold text-slate-900">Custom Data Sources</div>

              <div className="text-[11px] text-slate-500">

                Upload {FILE_UPLOAD_FORMATS_LABEL} files for agent knowledge.

              </div>

            </div>

            <Button size="sm" className="h-10 sm:h-8 w-full sm:w-auto shrink-0" onClick={() => openWizard(null)} data-testid="add-custom-source-btn">

              <Upload size={14} /> Add Knowledge Source

            </Button>

          </div>



          {sources.length === 0 ? (

            <div

              className="rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-6 text-center text-[13px] text-slate-600"

              data-testid="custom-sources-empty"

            >

              No custom knowledge sources yet. Click <strong>Add Knowledge Source</strong> to start the setup wizard.

            </div>

          ) : (

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

              {sources.map((s) => (

                <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4 min-w-0 overflow-hidden" data-testid={`custom-source-${s.id}`}>

                  <div className="flex items-start justify-between gap-2 min-w-0">

                    <div className="min-w-0 flex-1 overflow-hidden">

                      <div
                        className="text-[13.5px] font-semibold text-slate-900 truncate"
                        title={s.name || undefined}
                      >
                        {s.name}
                      </div>

                      <div className="text-[11px] text-slate-500 mt-0.5">

                        {s.document_count} doc(s) · {s.chunk_count} chunk(s)

                      </div>

                    </div>

                    <SourceStatusBadge
                      status={resolveBadgeStatus(s)}
                      label={syncLabel(s.openclaw_sync_status || s.status)}
                    />

                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">

                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => showDetail(s.id)}>Manage</Button>

                    {needsSetupContinuation(s) && (

                      <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => openSetup(s.id)}>

                        Continue setup

                      </Button>

                    )}

                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => handleSync(s.id)}>

                      <RefreshCw size={11} /> Sync

                    </Button>

                    <Button size="sm" variant="outline" className="h-7 text-[11px] text-rose-700" onClick={() => handleDeleteSource(s.id)}>

                      <Trash2 size={11} /> Delete

                    </Button>

                  </div>

                </div>

              ))}

            </div>

          )}

        </div>

      )}



      <KnowledgeSourceSetupWizard

        open={wizardOpen}

        onOpenChange={setWizardOpen}

        sources={sources}

        initialSourceId={wizardSourceId}

        onComplete={handleWizardComplete}

      />



      <CloudImportWizard

        open={cloudWizardOpen}

        onOpenChange={setCloudWizardOpen}

        provider={cloudProvider || "google_drive"}

        initialConnected={cloudInitialConnected}

        onComplete={silentRefresh}

      />



      <Dialog open={Boolean(detailId && detail)} onOpenChange={(o) => { if (!o) { setDetailId(null); setDetail(null); setSearchQ(""); setSearchHits([]); } }}>

        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-2xl max-h-[90dvh] min-w-0 overflow-x-hidden overflow-y-auto" data-testid="custom-source-detail">

          {detail && (

            <>

              <DialogHeader className="min-w-0 space-y-1.5">

                <DialogTitle className="pr-6 break-all leading-snug" title={detail.name || undefined}>
                  {detail.name}
                </DialogTitle>

                <DialogDescription>

                  {detail.document_count} document(s) · {detail.chunk_count} chunk(s) · {statusLabel(detail.status)}

                </DialogDescription>

              </DialogHeader>



              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-stretch sm:items-end">

                <div className="min-w-0">

                  <Label className="text-[12px]">Rename</Label>

                  <Input value={rename} onChange={(e) => setRename(e.target.value)} className="mt-1 h-10 sm:h-9 w-full min-w-0" />

                </div>

                <Button size="sm" variant="outline" className="h-10 sm:h-9 w-full sm:w-auto" onClick={handleRename} disabled={busy}><Pencil size={12} /> Save</Button>

              </div>



              <div className="rounded-lg border border-slate-200 p-3 space-y-1">

                <div className="flex items-center gap-2 text-[12px] font-semibold text-slate-800">

                  <Users size={13} /> Agent access

                </div>

                <p className="text-[12px] text-slate-600">

                  Available to all agents automatically.

                </p>

              </div>



              <div className="rounded-lg border border-slate-200 p-3 space-y-2">

                <div className="flex items-center gap-2 text-[12px] font-semibold text-slate-800">

                  <Search size={13} /> Search this knowledge base

                </div>

                <div className="flex gap-2">

                  <Input
                    value={searchQ}
                    onChange={(e) => {
                      const next = e.target.value;
                      setSearchQ(next);
                      if (!next.trim()) {
                        setSearchHits([]);
                      }
                    }}
                    placeholder="Search knowledge base…"
                    className="h-9"
                  />

                  <Button size="sm" onClick={handleSearch} disabled={busy}>Search</Button>

                </div>

                {searchHits.length > 0 && (

                  <div className="space-y-2 max-h-48 overflow-y-auto pr-1">

                    {searchHits.map((h, idx) => {
                      const cleaned = formatKbSearchResultContent(h.content);
                      if (!cleaned) return null;
                      return (
                        <div key={h.chunk_id || idx} className="rounded-lg bg-slate-50 border border-slate-200/80 p-2.5 text-[12px] leading-relaxed text-slate-700 min-w-0">

                          <div className="line-clamp-4 break-words font-normal text-slate-800">{cleaned}</div>

                        </div>
                      );
                    })}

                  </div>

                )}

              </div>



              <div className="space-y-2">

                <div className="flex items-center justify-between gap-2">

                  <div className="text-[12px] font-semibold text-slate-800">Documents</div>

                  <div>

                    <input

                      ref={uploadMoreRef}

                      type="file"

                      multiple

                      accept={FILE_UPLOAD_ACCEPT}

                      className="hidden"

                      onChange={async (e) => {

                        const { accepted, rejected } = partitionKnowledgeUploadFiles(e.target.files);

                        e.target.value = "";

                        if (rejected.length) {

                          toast.error(knowledgeUploadRejectToast(rejected));

                        }

                        if (!accepted.length || !detail?.id) return;

                        setBusy(true);

                        try {

                          const res = await brainApi.uploadCustomSource(detail.id, accepted);

                          const results = res?.results || [];

                          const ok = results.filter((r) => r.status === "ready");

                          const failed = results.filter((r) => r.status === "failed");

                          if (ok.length) {

                            toast.success(`Uploaded ${ok.length} file${ok.length === 1 ? "" : "s"}`);

                          }

                          if (failed.length) {

                            toast.error(

                              failed.map((r) => r.error || r.filename).filter(Boolean).join("; ")

                              || knowledgeUploadRejectToast(failed),

                            );

                          }

                          if (!results.length && !failed.length) {

                            toast.success(`Uploaded ${accepted.length} file${accepted.length === 1 ? "" : "s"}`);

                          }

                          await refreshDetail();

                          if (onChanged) await onChanged({ silent: true });

                        } catch (err) {

                          toast.error(err?.message || "Upload failed");

                        } finally {

                          setBusy(false);

                        }

                      }}

                    />

                    <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={busy} onClick={() => uploadMoreRef.current?.click()}>

                      <Upload size={12} className="mr-1" /> Upload more

                    </Button>

                  </div>

                </div>

                {detail.documents?.length ? (

                  <div className="overflow-x-auto">

                    <table className="w-full text-[11.5px]">

                      <thead>

                        <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">

                          <th className="py-1 pr-2">File</th>

                          <th className="py-1 pr-2">Type</th>

                          <th className="py-1 pr-2">Size</th>

                          <th className="py-1 pr-2">Status</th>

                          <th className="py-1 pr-2">Chunks</th>

                          <th className="py-1">Actions</th>

                        </tr>

                      </thead>

                      <tbody>

                        {detail.documents.map((d) => (

                          <tr key={d.id} className="border-t border-slate-100">

                            <td className="py-2 pr-2 font-medium max-w-[12rem] sm:max-w-[16rem]">
                              <span className="block truncate" title={d.original_filename || undefined}>
                                {d.original_filename}
                              </span>
                            </td>

                            <td className="py-2 pr-2">{d.file_ext}</td>

                            <td className="py-2 pr-2">{formatBytes(d.file_size_bytes)}</td>

                            <td className="py-2 pr-2">{statusLabel(d.status)}</td>

                            <td className="py-2 pr-2">{d.chunk_count}</td>

                            <td className="py-2">

                              <div className="flex gap-1">

                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Reprocess"

                                  onClick={async () => {

                                    setBusy(true);

                                    try {

                                      await brainApi.reprocessDocument(detail.id, d.id);

                                      toast.success("Reprocessed");

                                      await refreshDetail();

                                    } catch (e) {

                                      const msg = e?.message || "Reprocess failed";

                                      toast.error(/failed to fetch/i.test(msg)

                                        ? "Couldn't reach the API — check REACT_APP_BACKEND_URL and that the backend is running."

                                        : msg);

                                    }

                                    finally { setBusy(false); }

                                  }}>

                                  <RefreshCw size={12} />

                                </Button>

                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Download"

                                  onClick={async () => {

                                    try {

                                      await downloadBrainDocument(detail.id, d.id, d.original_filename);

                                    } catch (e) {

                                      toast.error(e?.message || "Download failed");

                                    }

                                  }}>

                                  <Download size={12} />

                                </Button>

                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-700" title="Delete"

                                  onClick={async () => {

                                    if (!window.confirm(`Delete ${d.original_filename}?`)) return;

                                    setBusy(true);

                                    try {

                                      await brainApi.deleteDocument(detail.id, d.id);

                                      clearProgress(detail.id);

                                      try {

                                        const updated = await brainApi.getCustomSource(detail.id);

                                        setDetail(updated);

                                        setRename(updated.name);

                                      } catch {

                                        // Last file of an unfinished setup removes the draft source.

                                        setDetailId(null);

                                        setDetail(null);

                                      }

                                      toast.success("Document deleted");

                                      await silentRefresh();

                                    } catch (e) { toast.error(e.message); }

                                    finally { setBusy(false); }

                                  }}>

                                  <Trash2 size={12} />

                                </Button>

                              </div>

                            </td>

                          </tr>

                        ))}

                      </tbody>

                    </table>

                  </div>

                ) : (

                  <div className="text-[12px] text-slate-500 flex items-center gap-2"><FileText size={14} /> No documents uploaded yet.</div>

                )}

              </div>

            </>

          )}

        </DialogContent>

      </Dialog>

    </>

  );

});

CustomSourceManager.displayName = "CustomSourceManager";
CustomSourceManager.propTypes = {
  sources: PropTypes.array,
  onChanged: PropTypes.func,
  headless: PropTypes.bool,
};

export default CustomSourceManager;

